import type { CSSProperties } from "react";

/** Row height, and the width of the sticky label column that names each row.
 *  Shared by the header, the rows and the unscheduled band so the two halves
 *  of the grid stay aligned. */
export const GANTT_ROW_HEIGHT = 34;
/** Starting width of the label column. Adjustable at runtime — see
 *  useResizablePanelWidth's `gantt-label-column` key — so treat this as the
 *  default rather than a constant every consumer can read. */
export const GANTT_LABEL_WIDTH = 260;
/** Narrowest the label column goes: the corner cell holds the granularity
 *  control and two icon buttons, and below this they start to clip. */
export const GANTT_LABEL_MIN_WIDTH = 240;
export const GANTT_LABEL_MAX_WIDTH = 560;
/** The axis row. Taller than the ticks need, because it now also carries the
 *  view's controls in its corner cell. */
export const GANTT_HEADER_HEIGHT = 40;
export const GANTT_BAR_HEIGHT = 18;
/** Grab width of a bar's resize handles. */
export const GANTT_HANDLE_PX = 8;
/** Height of a group band. Fixed rather than content-driven because the
 *  dependency arrows are positioned by arithmetic over the row order — a band
 *  whose height depends on its font would put every arrow below it out by a
 *  pixel or two per group. */
export const GANTT_GROUP_ROW_HEIGHT = 28;
/** Radius of the connector dot a link is dragged from, and how far it sits
 *  OUTSIDE the bar. The inner GANTT_HANDLE_PX belongs to the resize grips, so
 *  the two gestures never compete for the same pixels. */
export const GANTT_CONNECTOR_R = 4;
export const GANTT_CONNECTOR_OFFSET = 7;
/** How long a row takes to glide to a new place (a reorder, a collapse, a
 *  drop settling). The arrows tween over the same time and curve in JS, so the
 *  two must change together. */
export const GANTT_ROW_MOTION_MS = 200;
/** easeOutCubic — the CSS twin of `easeOutCubic` in useAnimatedRowTops. */
export const GANTT_ROW_MOTION_EASING = "cubic-bezier(0.33, 1, 0.68, 1)";

/**
 * Where a row or band header sits in the chart body. Rows are absolutely
 * placed and moved with a transform, so a reorder glides instead of jumping;
 * a row under the pointer follows it with no transition at all.
 */
export function ganttRowPlacement(top: number, followsPointer = false): CSSProperties {
  return {
    transform: `translate3d(0, ${top}px, 0)`,
    transition: followsPointer
      ? "none"
      : `transform ${GANTT_ROW_MOTION_MS}ms ${GANTT_ROW_MOTION_EASING}`,
  };
}
